import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { sendPasswordResetOtp } from "api/auth";
import { getApiErrorMessage } from "api/apiError";
import { Alert, Button, FormField, Input } from "Components/ui";
import { StepHeader } from "./AuthLayout";
import { useAuthFlow } from "./data";

const schema = z.object({
  userName: z.string().trim().min(1, "Enter your username"),
});

type ForgotValues = z.infer<typeof schema>;

const ForgotStep = () => {
  const flow = useAuthFlow();
  const [serverError, setServerError] = React.useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotValues>({
    resolver: zodResolver(schema),
    defaultValues: { userName: flow.userName },
  });

  const onSubmit = async ({ userName }: ForgotValues) => {
    setServerError(null);
    try {
      const res = await sendPasswordResetOtp(userName.trim());
      flow.startReset(userName.trim(), res.message || null);
    } catch (err) {
      setServerError(
        getApiErrorMessage(
          err,
          "We couldn't send a verification code. Please try again in a moment."
        )
      );
    }
  };

  return (
    <>
      <StepHeader
        back={{ label: "Back to sign in", onClick: () => flow.backToSignIn() }}
        step={{ current: 1, total: 2, label: "Reset password" }}
        title="Reset your password"
        description="Enter your username. We'll send a verification code to the WhatsApp number linked to your account."
      />
      <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {serverError && <Alert tone="danger">{serverError}</Alert>}
        <FormField label="Username" error={errors.userName?.message}>
          <Input
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            {...register("userName")}
          />
        </FormField>
        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          Send reset code
        </Button>
      </form>
    </>
  );
};

export default ForgotStep;
